import ServiceUnavailableAlert from 'components/core/ServiceUnavailableAlert';
import BuscaPacienteForm from 'components/paciente/BuscaPacienteForm';
import ModalPaciente from 'components/paciente/ModalPaciente';
import TabelaPaciente from 'components/paciente/TabelaPaciente';
import { usePacientes } from 'hooks/usePacientes';
import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Card } from 'react-bootstrap';
import { FaPlus } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { selectDoctor, selectFunc } from 'redux/features/auth/authSlice';
import styles from 'styles/styles.module.css';
import { resolveUsuarioAgenda } from 'utils/agenda';
import { isInfrastructureError } from 'utils/apiErrors';
import {
  buildPacientesSearchSnapshot,
  FILTROS_PACIENTES_VAZIOS,
  filtrosIguais,
  getInitialSearchFromLocation,
  normalizeFiltrosForApi,
  validateBuscaPacientes,
} from 'utils/pacientesSearch';

export default function FormPaciente({ show = false, onHide = () => {}, onSelectPaciente = null }) {
  const location = useLocation();
  const initialSearch = getInitialSearchFromLocation(location);
  const embedded = Boolean(onSelectPaciente);

  const [filtrosDraft, setFiltrosDraft] = useState(initialSearch.filtros);
  const [filtrosBusca, setFiltrosBusca] = useState(initialSearch.filtros);
  const [showTablePaciente, setShowTablePaciente] = useState(initialSearch.showTable);
  const [buscaErro, setBuscaErro] = useState('');
  const [invalidField, setInvalidField] = useState('');
  const skipAutoOpenModalRef = useRef(initialSearch.skipAutoOpenModal);
  const [modalState, setModalState] = useState({
    show: false,
    mode: 'criar',
    paciente: null,
  });

  const autoOpenedRef = useRef(false);

  const func = useSelector(selectFunc);
  const doctor = useSelector(selectDoctor);
  const { codFunc, nome } = resolveUsuarioAgenda({ func, doctor });

  const {
    pacientes,
    isLoading: isLoadingPacientes,
    isValidating: isValidatingPacientes,
    error: errorPacientes,
    mutate: mutatePacientes,
  } = usePacientes(filtrosBusca);

  const isSearching = isLoadingPacientes || isValidatingPacientes;
  const filtrosDesatualizados = showTablePaciente && !filtrosIguais(filtrosDraft, filtrosBusca);
  const showEmptyResults =
    showTablePaciente &&
    !isSearching &&
    !errorPacientes &&
    pacientes.length === 0 &&
    filtrosIguais(filtrosDraft, filtrosBusca);
  const showHintInicial = !showTablePaciente && !buscaErro;

  useEffect(() => {
    const restored = getInitialSearchFromLocation(location);
    setFiltrosDraft(restored.filtros);
    setFiltrosBusca(restored.filtros);
    setShowTablePaciente(restored.showTable);
    skipAutoOpenModalRef.current = restored.skipAutoOpenModal;
    setBuscaErro('');
    setInvalidField('');
    if (restored.fromAtividades) {
      autoOpenedRef.current = true;
    } else if (!restored.filtros.codPac) {
      autoOpenedRef.current = false;
    }
  }, [location.key, location.state]);

  useEffect(() => {
    if (autoOpenedRef.current || skipAutoOpenModalRef.current) return;
    if (filtrosBusca.codPac && pacientes?.length === 1) {
      autoOpenedRef.current = true;
      setModalState({ show: true, mode: 'editar', paciente: pacientes[0] });
    }
  }, [pacientes, filtrosBusca.codPac]);

  function handleNovoPaciente() {
    setModalState({ show: true, mode: 'criar', paciente: null });
  }

  function handleEditarPaciente(pac) {
    setModalState({ show: true, mode: 'editar', paciente: pac });
  }

  function handleFecharModal() {
    setModalState((prev) => ({ ...prev, show: false, paciente: null }));
  }

  function handleSucessoModal(pacienteSalvo) {
    mutatePacientes();

    if (!pacienteSalvo?.CODPAC) return;

    setModalState({
      show: true,
      mode: 'editar',
      paciente: pacienteSalvo,
    });
  }

  function executarBusca(filtros) {
    const validation = validateBuscaPacientes(filtros);
    if (!validation.valid) {
      setBuscaErro(validation.message);
      setInvalidField(validation.invalidField || '');
      return;
    }

    setBuscaErro('');
    setInvalidField('');
    autoOpenedRef.current = false;

    const normalizado = normalizeFiltrosForApi(filtros);
    setFiltrosDraft(normalizado);
    setFiltrosBusca(normalizado);
    setShowTablePaciente(true);
  }

  function handleLimparBusca() {
    setFiltrosDraft({ ...FILTROS_PACIENTES_VAZIOS });
    setFiltrosBusca({ ...FILTROS_PACIENTES_VAZIOS });
    setShowTablePaciente(false);
    setBuscaErro('');
    setInvalidField('');
    autoOpenedRef.current = false;
  }

  function handleSubmitBusca(e) {
    e.preventDefault();
    handleFecharModal();
    executarBusca(filtrosDraft);
  }

  return (
    <>
      <section
        className={`${styles.homePageFonts}`}
        style={{
          minHeight: embedded ? undefined : '100vh',
          padding: embedded ? '0' : '42px 30px 48px',
          background: embedded ? undefined : 'var(--background-color)',
        }}
      >
        {!embedded && (
          <header className="d-flex flex-column flex-lg-row justify-content-between align-items-start gap-2 mb-4">
            <div>
              <h1 className="fs-3">Cadastro do Paciente</h1>
              <p style={{ color: 'var(--font-gray)' }}>
                Busque um paciente existente ou inicie um novo cadastro.
              </p>
            </div>
            <Button
              type="button"
              variant="outline-primary"
              size="sm"
              className="rounded-pill fw-bold px-3 py-2"
              onClick={handleNovoPaciente}
            >
              <FaPlus size={10} className="me-2" aria-hidden="true" />
              Criar Novo
            </Button>
          </header>
        )}

        <Card
          style={{
            border: '1px solid var(--neutral-200)',
            borderRadius: embedded ? 12 : 20,
            boxShadow: 'none',
            background: '#fff',
          }}
        >
          <Card.Body style={{ padding: embedded ? '1rem' : '30px 40px' }}>
            <p
              className="fw-semibold text-uppercase"
              style={{
                color: 'var(--font-gray-light)',
                letterSpacing: '0.2em',
              }}
            >
              Buscar Paciente
            </p>

            <BuscaPacienteForm
              filtros={filtrosDraft}
              onChange={setFiltrosDraft}
              onSubmit={handleSubmitBusca}
              onLimpar={handleLimparBusca}
              isLoading={isSearching}
              buscaErro={buscaErro}
              invalidField={invalidField}
              embedded={embedded}
            />

            {showHintInicial && (
              <p className="text-muted small text-center mt-3 mb-0">
                Preencha um filtro e clique em Localizar.
              </p>
            )}

            {filtrosDesatualizados && (
              <Alert variant="warning" className="mt-3 mb-0 py-2 small">
                Filtros alterados. Clique em <strong>Localizar</strong> para atualizar a lista.
              </Alert>
            )}

            {errorPacientes &&
              (isInfrastructureError(errorPacientes) ? (
                <ServiceUnavailableAlert
                  error={errorPacientes}
                  stale={pacientes.length > 0}
                  onRetry={() => mutatePacientes()}
                />
              ) : (
                <Alert variant="danger" className="mt-3 text-center">
                  {errorPacientes?.response?.data?.errors?.[0] ||
                    errorPacientes?.response?.data?.message ||
                    'Erro ao buscar pacientes.'}
                  <div className="mt-2">
                    <Button variant="outline-danger" size="sm" onClick={() => mutatePacientes()}>
                      Tentar novamente
                    </Button>
                  </div>
                </Alert>
              ))}

            {showEmptyResults && (
              <Alert variant="info" className="mt-3 text-center mb-0">
                Nenhum paciente encontrado com esses filtros.
                <div className="mt-2">
                  <Button variant="outline-secondary" size="sm" onClick={handleLimparBusca}>
                    Limpar filtros
                  </Button>
                </div>
              </Alert>
            )}

            <TabelaPaciente
              onSelectPaciente={onSelectPaciente}
              showTablePaciente={showTablePaciente}
              pacientes={pacientes}
              onEditar={handleEditarPaciente}
              pacientesSearchRestore={
                onSelectPaciente
                  ? null
                  : buildPacientesSearchSnapshot(filtrosBusca, showTablePaciente)
              }
            />
          </Card.Body>
        </Card>
      </section>

      <ModalPaciente
        show={modalState.show}
        onHide={handleFecharModal}
        mode={modalState.mode}
        paciente={modalState.paciente}
        codFunc={codFunc}
        nomeFuncLogado={nome}
        onSucesso={handleSucessoModal}
      />
    </>
  );
}
